import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useResource } from "../api/resource";
import { EmptyState, ErrorState, Notice, PageHeader, SkeletonBlock, SkeletonRows, StatusBadge } from "../components/ui";
import { keys, useDossier } from "../dossier";
import { formatDateTime, locale, w } from "../i18n";
import { deriveIssues, type Severity } from "../issues";
import type { AuditEventItem } from "../api/types";

const SEVERITY_STATUS = { high: "failed", medium: "needs_review", low: "not_started" } as const;

export function IssuesPage() {
  const { dossier, documents, readiness, inspect } = useDossier();
  const [severity, setSeverity] = useState<Severity | "all">("all");
  const [scope, setScope] = useState("all");
  const lang = locale();
  const audits = useResource(keys.reportAudits(dossier.id, lang), () => api.reportAudits(dossier.id, lang));
  const all = deriveIssues(documents.data, readiness.data, audits.data);
  const scopes = [...new Set(all.map(issue => issue.scope))];
  const visible = all.filter(issue => (severity === "all" || issue.severity === severity) && (scope === "all" || issue.scope === scope));
  const loading = documents.loading || readiness.loading;

  return <>
    <PageHeader title={w("issues.title")} lead={w("issues.lead")} />
    {(documents.error || readiness.error) && <ErrorState title={w("common.genericError")} error={documents.error || readiness.error}
      onRetry={() => { documents.reload(); readiness.reload(); }} />}
    <div className="ws-row" style={{ marginBottom: 12 }}>
      <label className="ws-small ws-muted" htmlFor="f-sev">{w("issues.col.severity")}</label>
      <select id="f-sev" className="ws-select" value={severity} onChange={e => setSeverity(e.target.value as Severity | "all")}>
        <option value="all">{w("docs.filter.all")}</option>{(["high", "medium", "low"] as Severity[]).map(s => <option key={s} value={s}>{w(`issues.severity.${s}`)}</option>)}</select>
      <label className="ws-small ws-muted" htmlFor="f-scope" style={{ marginLeft: 12 }}>{w("issues.col.scope")}</label>
      <select id="f-scope" className="ws-select" value={scope} onChange={e => setScope(e.target.value)}>
        <option value="all">{w("docs.filter.all")}</option>{scopes.map(s => <option key={s} value={s}>{s}</option>)}</select>
    </div>
    <div className="ws-card ws-table-wrap"><table className="ws-table">
      <thead><tr><th>{w("issues.col.severity")}</th><th>{w("issues.col.scope")}</th><th>{w("issues.col.issue")}</th><th><span className="ws-sr-only">{w("issues.col.action")}</span></th></tr></thead>
      <tbody>
        {loading && <SkeletonRows columns={[70, 50, "70%", 50]} />}
        {visible.map(issue => <tr key={issue.id}>
          <td><StatusBadge status={SEVERITY_STATUS[issue.severity]} label={w(`issues.severity.${issue.severity}`)} /></td>
          <td className="ws-mono">{issue.scope}</td><td>{issue.message}</td>
          <td style={{ textAlign: "right" }}>{issue.documentId
            ? <button className="ws-btn sm" onClick={() => inspect(issue.documentId!)}>{w("common.open")}</button>
            : <Link className="ws-btn sm" to={`/dossiers/${dossier.id}/sections/${issue.sectionCode}`}>{w("common.open")}</Link>}</td>
        </tr>)}
      </tbody></table>
      {!loading && !visible.length && <EmptyState title={w("issues.empty")} />}
    </div>
    <div style={{ marginTop: 16 }}><Notice tone="neutral">{w("issues.auditsNote")}{" "}
      <Link className="ws-link" to={`/dossiers/${dossier.id}/audits`}>{w("issues.openAudits")}</Link></Notice></div>
  </>;
}

export function HistoryPage() {
  const { dossier, inspect } = useDossier();
  const jobs = useResource(keys.jobs(dossier.id), () => api.jobs(dossier.id));
  return <>
    <PageHeader title={w("history.title")} lead={w("history.lead")} />
    {jobs.error && <ErrorState title={w("common.genericError")} error={jobs.error} onRetry={jobs.reload} />}
    {jobs.loading && <SkeletonBlock lines={6} />}
    {jobs.data && !jobs.data.length && <div className="ws-card"><EmptyState title={w("history.empty")} /></div>}
    {jobs.data && jobs.data.length > 0 && <ul className="ws-card ws-timeline">{jobs.data.map(job => {
      const duration = job.started_at && job.finished_at ? ((Date.parse(job.finished_at) - Date.parse(job.started_at)) / 1000).toFixed(1) : null;
      const status = job.status === "succeeded" ? "ready" : job.status === "failed" ? "failed" : "processing";
      return <li key={job.id}><time dateTime={job.created_at}>{formatDateTime(job.created_at)}</time>
        <div>
          <div className="ws-row" style={{ justifyContent: "space-between" }}>
            <button className="ws-doc-name" onClick={() => inspect(job.document_id)}>{job.document_name}</button>
            <StatusBadge status={status} />
          </div>
          <div className="ws-small ws-muted">{w("panel.attempt", { n: job.attempt })}{duration && ` · ${w("history.duration", { seconds: duration })}`}
            {job.error_message && ` · ${job.error_message}`}</div>
          <details className="ws-small"><summary>{w("history.details")}</summary>
            <ul>{job.stages.map((stage, index) => <li key={index}>{w(`stage.${stage.name}`)} : {stage.status}
              {stage.duration_ms != null ? ` · ${stage.duration_ms} ms` : ""}{stage.detail ? ` · ${stage.detail}` : ""}</li>)}</ul>
            <span className="ws-faint">{job.id}{job.error_code ? ` · ${job.error_code}` : ""}</span></details>
        </div></li>;
    })}</ul>}
  </>;
}

type AuditCategory = "all" | "documents" | "drafting" | "review" | "corrections" | "dossier";
const AUDIT_CATEGORIES: AuditCategory[] = ["all", "documents", "drafting", "review", "corrections", "dossier"];
const CORRECTIONS = ["document.metadata_corrected", "document.removed", "document.restored"];

function categoryOf(action: string): AuditCategory {
  if (action.startsWith("generation.") || action.startsWith("chapter_generation.")) return "drafting";
  if (action.startsWith("section.") || action.startsWith("plan.") || action.startsWith("request.")) return "review";
  if (action.startsWith("team.") || action.startsWith("schedule.")) return "dossier";
  if (CORRECTIONS.includes(action)) return "corrections";
  if (action.startsWith("document.")) return "documents";
  return "dossier";
}

function actionLabel(action: string): string {
  const label = w(`audit.action.${action}`);
  return label === `audit.action.${action}` ? action : label;
}

const blockState = (state: unknown) => w(`draft.state.${String(state || "empty")}`);

function describe(event: AuditEventItem): string {
  const details = event.details;
  const parts: string[] = [];
  if (event.target_type === "section" || event.target_type === "chapter") parts.push(event.target_id);
  if (details.filename) parts.push(String(details.filename));
  if (details.version_number) parts.push(`v${details.version_number}`);
  if (details.document_type) parts.push(String(details.document_type));
  if (details.status) parts.push(String(details.status));
  if (details.block_status) parts.push(blockState(details.block_status));
  if (details.model) parts.push(String(details.model));
  if (typeof details.duration_ms === "number") parts.push(w("history.duration", { seconds: (details.duration_ms / 1000).toFixed(1) }));
  if (details.error_code) parts.push(String(details.error_code));
  const before = details.before as Record<string, unknown> | undefined, after = details.after as Record<string, unknown> | undefined;
  if (before && after) parts.push(Object.keys(after).map(key => `${key}: ${before[key] ?? w("common.notAvailable")} → ${after[key] ?? w("common.notAvailable")}`).join(", "));
  return parts.join(" · ");
}

type RunSections = Record<string, { block_status?: string; findings?: number; gaps?: number; error?: string | null }>;

/** A chapter run's outcome per section, folded under the chapter event. */
function ChapterRunDetails({ sections }: { sections: RunSections }) {
  const entries = Object.entries(sections);
  const counts = entries.reduce<Record<string, number>>((acc, [, s]) => {
    const state = s.error ? "failed" : s.block_status || "empty";
    acc[state] = (acc[state] || 0) + 1;
    return acc;
  }, {});
  return <details className="ws-small ws-audit-group">
    <summary className="ws-link">{w("audit.runSummary", { count: entries.length,
      states: Object.entries(counts).map(([state, n]) => `${n} ${state === "failed" ? w("status.failed").toLowerCase() : blockState(state).toLowerCase()}`).join(", ") })}</summary>
    <ul>{entries.map(([code, s]) => <li key={code}><span className="ws-mono">{code}</span> : {s.error ? w("status.failed") : blockState(s.block_status)}
      {s.findings ? ` · ${w("audit.findings", { count: s.findings })}` : ""}{s.gaps ? ` · ${w("audit.gaps", { count: s.gaps })}` : ""}</li>)}</ul>
  </details>;
}

export function AuditTrailPage() {
  const { dossier } = useDossier();
  const events = useResource(keys.audit(dossier.id), () => api.auditEvents(dossier.id));
  const [category, setCategory] = useState<AuditCategory>("all");
  const [actor, setActor] = useState<"all" | "user" | "system">("all");
  const all = events.data || [];
  // Section runs of a chapter run are summarised on the chapter event, not listed one by one.
  const chapterRuns = new Set(all.filter(e => e.action === "chapter_generation.completed" || e.action === "chapter_generation.failed")
    .map(e => String(e.details.chapter_run_id)));
  const visible = all.filter(e => !(e.action.startsWith("generation.") && e.details.chapter_run_id && chapterRuns.has(String(e.details.chapter_run_id))))
    .filter(e => category === "all" || categoryOf(e.action) === category)
    .filter(e => actor === "all" || (actor === "system") === e.actor.startsWith("system:"));

  return <>
    <PageHeader title={w("audit.title")} lead={w("audit.lead")} />
    {events.error && <ErrorState title={w("common.genericError")} error={events.error} onRetry={events.reload} />}
    <div className="ws-row" style={{ marginBottom: 12 }}>
      <label className="ws-small ws-muted" htmlFor="f-cat">{w("audit.filter.category")}</label>
      <select id="f-cat" className="ws-select" value={category} onChange={e => setCategory(e.target.value as AuditCategory)}>
        {AUDIT_CATEGORIES.map(c => <option key={c} value={c}>{w(`audit.category.${c}`)}</option>)}</select>
      <label className="ws-small ws-muted" htmlFor="f-actor" style={{ marginLeft: 12 }}>{w("audit.filter.actor")}</label>
      <select id="f-actor" className="ws-select" value={actor} onChange={e => setActor(e.target.value as "all" | "user" | "system")}>
        {(["all", "user", "system"] as const).map(a => <option key={a} value={a}>{w(`audit.actor.${a}`)}</option>)}</select>
      {events.data && <span className="ws-small ws-faint" style={{ marginLeft: "auto" }}>{w("audit.count", { count: visible.length })}</span>}
    </div>
    {events.loading && <SkeletonBlock lines={6} />}
    {events.data && !visible.length && <div className="ws-card"><EmptyState title={w("audit.empty")} /></div>}
    {visible.length > 0 && <ul className="ws-card ws-timeline">{visible.map(event => <li key={event.id}>
      <time dateTime={event.created_at}>{formatDateTime(event.created_at)}</time>
      <div><strong>{actionLabel(event.action)}</strong>
        <div className="ws-small ws-muted">{event.actor.startsWith("system:") ? w("audit.system") : event.actor}
          {describe(event) && ` · ${describe(event)}`}</div>
        {event.details.sections && typeof event.details.sections === "object" &&
          <ChapterRunDetails sections={event.details.sections as RunSections} />}
      </div>
    </li>)}</ul>}
  </>;
}
